/**
 * Tech User Management Page
 *
 * Admin only - manage contractors and tech visibility in dropdowns.
 */

import { useState, useEffect, useCallback } from 'react';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthProvider';
import { getAdminConfig, updateContractors, updateHiddenResources, ContractorConfig, getResources } from '../services/api';
import { Resource } from '../services/types';

export default function TechUserManagement() {
  const { user } = useAuth();
  const [contractors, setContractors] = useState<ContractorConfig[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [hiddenResourceIds, setHiddenResourceIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // New contractor form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newContractor, setNewContractor] = useState<ContractorConfig>({
    name: '',
    email: '',
    timetechUserId: 0,
  });

  // Edit contractor
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editContractor, setEditContractor] = useState<ContractorConfig | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [config, resourceList] = await Promise.all([
        getAdminConfig(),
        getResources(),
      ]);
      setContractors(config.contractors);
      setHiddenResourceIds(config.hiddenResourceIds || []);
      setResources(resourceList);
    } catch (err) {
      console.error('Failed to load config:', err);
      // Use defaults if API unavailable
      setContractors([
        { name: 'Nick Metnik', email: 'nick@umetech.net', timetechUserId: 4 },
        { name: 'Dale Badore', email: 'dbadore@umetech.net', timetechUserId: 65 },
        { name: 'Arafat Jamil', email: 'ajamil@umetech.net', timetechUserId: 1000004 },
      ]);
      setHiddenResourceIds([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddContractor = async () => {
    if (!newContractor.name.trim() || !newContractor.email.trim()) {
      setError('Name and email are required');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const updated = [...contractors, newContractor];
      await updateContractors(updated);
      setContractors(updated);
      setNewContractor({ name: '', email: '', timetechUserId: 0 });
      setShowAddForm(false);
      setSuccess('Contractor added successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add contractor');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateContractor = async () => {
    if (editingIndex === null || !editContractor) return;

    if (!editContractor.name.trim() || !editContractor.email.trim()) {
      setError('Name and email are required');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const updated = [...contractors];
      updated[editingIndex] = editContractor;
      await updateContractors(updated);
      setContractors(updated);
      setEditingIndex(null);
      setEditContractor(null);
      setSuccess('Contractor updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update contractor');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContractor = async (index: number) => {
    if (!confirm(`Are you sure you want to remove ${contractors[index].name}?`)) return;

    setSaving(true);
    setError(null);
    try {
      const updated = contractors.filter((_, i) => i !== index);
      await updateContractors(updated);
      setContractors(updated);
      setSuccess('Contractor removed successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove contractor');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (index: number) => {
    setEditingIndex(index);
    setEditContractor({ ...contractors[index] });
    setShowAddForm(false);
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditContractor(null);
  };

  const handleToggleResourceVisibility = async (resourceId: number) => {
    setSaving(true);
    setError(null);
    try {
      const isCurrentlyHidden = hiddenResourceIds.includes(resourceId);
      const updated = isCurrentlyHidden
        ? hiddenResourceIds.filter(id => id !== resourceId)
        : [...hiddenResourceIds, resourceId];

      await updateHiddenResources(updated);
      setHiddenResourceIds(updated);
      setSuccess(isCurrentlyHidden ? 'Resource is now visible in dropdowns' : 'Resource is now hidden from dropdowns');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update visibility');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader userEmail={user?.email} />

      <div className="max-w-4xl mx-auto py-6 px-4">
        <h1 className="text-2xl font-bold text-white mb-2">Tech User Management</h1>
        <p className="text-slate-400 mb-6">Manage contractors and configure which users appear in dropdowns.</p>

        {/* Messages */}
        {error && (
          <div className="mb-4 bg-red-900/30 border border-red-600/50 rounded-lg p-3 text-red-300">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 bg-green-900/30 border border-green-600/50 rounded-lg p-3 text-green-300">
            {success}
          </div>
        )}

        {loading ? (
          <div className="text-slate-400">Loading...</div>
        ) : (
          <div className="space-y-8">
            {/* Contractors Section */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-white">Contractors</h2>
                  <p className="text-sm text-slate-400 mt-1">
                    Contractors appear in the Calendar tech selector for admins.
                  </p>
                </div>
                {!showAddForm && editingIndex === null && (
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Contractor
                  </button>
                )}
              </div>

              {/* Add Contractor Form */}
              {showAddForm && (
                <div className="mb-4 bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                  <h3 className="text-sm font-medium text-white mb-3">Add New Contractor</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Name *</label>
                      <input
                        type="text"
                        value={newContractor.name}
                        onChange={(e) => setNewContractor({ ...newContractor, name: e.target.value })}
                        placeholder="John Smith"
                        className="w-full bg-slate-700 text-white rounded px-3 py-2 text-sm border border-slate-600 focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Email *</label>
                      <input
                        type="email"
                        value={newContractor.email}
                        onChange={(e) => setNewContractor({ ...newContractor, email: e.target.value })}
                        placeholder="john@example.com"
                        className="w-full bg-slate-700 text-white rounded px-3 py-2 text-sm border border-slate-600 focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">TimeTech User ID</label>
                      <input
                        type="number"
                        value={newContractor.timetechUserId || ''}
                        onChange={(e) => setNewContractor({ ...newContractor, timetechUserId: parseInt(e.target.value) || 0 })}
                        placeholder="0"
                        className="w-full bg-slate-700 text-white rounded px-3 py-2 text-sm border border-slate-600 focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => { setShowAddForm(false); setNewContractor({ name: '', email: '', timetechUserId: 0 }); }}
                      className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg text-sm transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddContractor}
                      disabled={saving}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      {saving ? 'Adding...' : 'Add Contractor'}
                    </button>
                  </div>
                </div>
              )}

              {/* Contractors List */}
              <div className="space-y-2">
                {contractors.length === 0 ? (
                  <div className="text-slate-500 text-sm py-4 text-center">No contractors configured</div>
                ) : (
                  contractors.map((c, idx) => (
                    <div key={idx} className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
                      {editingIndex === idx && editContractor ? (
                        /* Edit Mode */
                        <div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs text-slate-400 mb-1">Name *</label>
                              <input
                                type="text"
                                value={editContractor.name}
                                onChange={(e) => setEditContractor({ ...editContractor, name: e.target.value })}
                                className="w-full bg-slate-700 text-white rounded px-3 py-2 text-sm border border-slate-600 focus:border-blue-500 focus:outline-none"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-400 mb-1">Email *</label>
                              <input
                                type="email"
                                value={editContractor.email}
                                onChange={(e) => setEditContractor({ ...editContractor, email: e.target.value })}
                                className="w-full bg-slate-700 text-white rounded px-3 py-2 text-sm border border-slate-600 focus:border-blue-500 focus:outline-none"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-400 mb-1">TimeTech User ID</label>
                              <input
                                type="number"
                                value={editContractor.timetechUserId || ''}
                                onChange={(e) => setEditContractor({ ...editContractor, timetechUserId: parseInt(e.target.value) || 0 })}
                                className="w-full bg-slate-700 text-white rounded px-3 py-2 text-sm border border-slate-600 focus:border-blue-500 focus:outline-none"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2 mt-3">
                            <button
                              onClick={cancelEdit}
                              className="px-3 py-1.5 bg-slate-600 hover:bg-slate-500 text-white rounded text-sm transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleUpdateContractor}
                              disabled={saving}
                              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium transition-colors disabled:opacity-50"
                            >
                              {saving ? 'Saving...' : 'Save'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* View Mode */
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-white font-medium">{c.name}</span>
                            <span className="text-slate-400 ml-2 text-sm">({c.email})</span>
                            {c.timetechUserId > 0 && (
                              <span className="text-xs text-slate-500 ml-2">TimeTech ID: {c.timetechUserId}</span>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => startEdit(idx)}
                              className="p-1.5 text-slate-400 hover:text-blue-400 hover:bg-slate-600 rounded transition-colors"
                              title="Edit"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteContractor(idx)}
                              className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-slate-600 rounded transition-colors"
                              title="Delete"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* AutoTask Technicians Section */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-white mb-2">AutoTask Technicians</h2>
              <p className="text-sm text-slate-400 mb-4">
                Toggle visibility to hide techs from Calendar and other dropdowns.
                Hidden techs are shown with a strikethrough.
              </p>
              <div className="space-y-1 max-h-80 overflow-y-auto">
                {resources
                  .filter(r => r.isActive !== false)
                  .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`))
                  .map((r) => {
                    const isHidden = hiddenResourceIds.includes(r.autoTaskId);
                    return (
                      <div
                        key={r.id}
                        className={`flex items-center justify-between rounded px-3 py-2 ${
                          isHidden ? 'bg-red-900/20 border border-red-800/30' : 'bg-slate-700/30'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleToggleResourceVisibility(r.autoTaskId)}
                            disabled={saving}
                            className={`w-10 h-5 rounded-full relative transition-colors ${
                              isHidden ? 'bg-red-600' : 'bg-green-600'
                            } ${saving ? 'opacity-50' : ''}`}
                            title={isHidden ? 'Click to show in dropdowns' : 'Click to hide from dropdowns'}
                          >
                            <span
                              className={`absolute w-4 h-4 bg-white rounded-full top-0.5 transition-all ${
                                isHidden ? 'left-0.5' : 'left-5'
                              }`}
                            />
                          </button>
                          <div>
                            <span className={`${isHidden ? 'text-slate-500 line-through' : 'text-white'}`}>
                              {r.firstName} {r.lastName}
                            </span>
                            {r.email && (
                              <span className="text-slate-500 text-sm ml-2">({r.email})</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isHidden && (
                            <span className="text-[10px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded">
                              HIDDEN
                            </span>
                          )}
                          <span className="text-xs text-slate-500">ID: {r.autoTaskId}</span>
                        </div>
                      </div>
                    );
                  })}
              </div>
              <p className="text-xs text-slate-500 mt-4">
                AutoTask technicians are synced automatically. To add or remove, update them in AutoTask PSA.
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
